import React, { useState } from 'react';
import { BellIcon, ShieldIcon, KeyIcon, GlobeIcon, CreditCardIcon, UserIcon, MailIcon, SmartphoneIcon, CheckIcon, XIcon, PlusIcon, TrashIcon, EyeIcon, EyeOffIcon, AlertCircleIcon } from 'lucide-react';
type TabType = 'profile' | 'billing' | 'privacy' | 'security' | 'notifications';
export const Settings = () => {
  const [activeTab, setActiveTab] = useState<TabType>('profile');
  const [showPassword, setShowPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  // Profile state
  const [profileData, setProfileData] = useState({
    displayName: 'John Doe',
    email: 'john@example.com',
    phone: '+1 (555) 123-4567',
    bio: 'AI enthusiast and developer',
    location: 'San Francisco, CA',
    website: 'https://johndoe.com'
  });
  // Privacy state
  const [privacySettings, setPrivacySettings] = useState({
    profileVisibility: 'public',
    showEmail: false,
    showApps: true,
    allowMessages: true,
    dataSharing: false,
    analytics: true
  });
  // Security state
  const [securitySettings, setSecuritySettings] = useState({
    twoFactorEnabled: false,
    loginAlerts: true,
    sessionTimeout: '30'
  });
  // Notification state
  const [notificationSettings, setNotificationSettings] = useState({
    emailNotifications: true,
    appUpdates: true,
    newFeatures: true,
    marketingEmails: false,
    weeklyDigest: true,
    securityAlerts: true
  });
  const tabs = [{
    id: 'profile' as TabType,
    label: 'Profile',
    icon: <UserIcon className="h-5 w-5" data-id="element-2013" />
  }, {
    id: 'billing' as TabType,
    label: 'Billing',
    icon: <CreditCardIcon className="h-5 w-5" data-id="element-2014" />
  }, {
    id: 'privacy' as TabType,
    label: 'Privacy',
    icon: <ShieldIcon className="h-5 w-5" data-id="element-2015" />
  }, {
    id: 'security' as TabType,
    label: 'Security',
    icon: <KeyIcon className="h-5 w-5" data-id="element-2016" />
  }, {
    id: 'notifications' as TabType,
    label: 'Notifications',
    icon: <BellIcon className="h-5 w-5" data-id="element-2017" />
  }];
  const paymentMethods = [{
    id: '1',
    type: 'Visa',
    last4: '4242',
    expiry: '12/25',
    isDefault: true
  }, {
    id: '2',
    type: 'Mastercard',
    last4: '8888',
    expiry: '09/24',
    isDefault: false
  }];
  const billingHistory = [{
    id: '1',
    date: 'June 1, 2024',
    amount: '$9.99',
    status: 'Paid',
    invoice: 'INV-001'
  }, {
    id: '2',
    date: 'May 1, 2024',
    amount: '$9.99',
    status: 'Paid',
    invoice: 'INV-002'
  }, {
    id: '3',
    date: 'April 1, 2024',
    amount: '$9.99',
    status: 'Paid',
    invoice: 'INV-003'
  }];
  const activeSessions = [{
    id: '1',
    device: 'Chrome on MacBook Pro',
    location: 'San Francisco, CA',
    lastActive: '2 minutes ago',
    current: true
  }, {
    id: '2',
    device: 'Safari on iPhone 14',
    location: 'San Francisco, CA',
    lastActive: '1 hour ago',
    current: false
  }];
  return <div className="min-h-screen bg-gray-50 py-8 px-4 sm:px-6 lg:px-8" data-id="element-2018">
      <div className="max-w-6xl mx-auto" data-id="element-2019">
        <h1 className="text-2xl font-bold mb-8" data-id="element-2020">Settings</h1>
        <div className="flex flex-col lg:flex-row gap-6" data-id="element-2021">
          {/* Sidebar Navigation */}
          <div className="lg:w-64 flex-shrink-0" data-id="element-2022">
            <div className="bg-white rounded-lg border border-gray-200 overflow-hidden" data-id="element-2023">
              {tabs.map(tab => <button key={tab.id} onClick={() => setActiveTab(tab.id)} className={`w-full flex items-center px-4 py-3 border-b border-gray-200 last:border-b-0 transition-colors ${activeTab === tab.id ? 'bg-indigo-50 text-indigo-600' : 'text-gray-700 hover:bg-gray-50'}`} data-id="element-2024">
                  {tab.icon}
                  <span className="ml-3 font-medium" data-id="element-2025">{tab.label}</span>
                </button>)}
            </div>
          </div>
          {/* Content Area */}
          <div className="flex-1" data-id="element-2026">
            {/* Profile Tab */}
            {activeTab === 'profile' && <div className="bg-white rounded-lg border border-gray-200 p-6" data-id="element-2027">
                <h2 className="text-xl font-semibold mb-6" data-id="element-2028">Profile Settings</h2>
                <div className="space-y-6" data-id="element-2029">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-2030">
                    <div data-id="element-2031">
                      <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-2032">
                        Display Name
                      </label>
                      <input type="text" value={profileData.displayName} onChange={e => setProfileData({
                    ...profileData,
                    displayName: e.target.value
                  })} className="w-full border border-gray-300 rounded-md px-4 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500" data-id="element-2033" />
                    </div>
                    <div data-id="element-2034">
                      <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-2035">
                        Email Address
                      </label>
                      <input type="email" value={profileData.email} onChange={e => setProfileData({
                    ...profileData,
                    email: e.target.value
                  })} className="w-full border border-gray-300 rounded-md px-4 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500" data-id="element-2036" />
                    </div>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-2037">
                    <div data-id="element-2038">
                      <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-2039">
                        Phone Number
                      </label>
                      <input type="tel" value={profileData.phone} onChange={e => setProfileData({
                    ...profileData,
                    phone: e.target.value
                  })} className="w-full border border-gray-300 rounded-md px-4 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500" data-id="element-2040" />
                    </div>
                    <div data-id="element-2041">
                      <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-2042">
                        Location
                      </label>
                      <input type="text" value={profileData.location} onChange={e => setProfileData({
                    ...profileData,
                    location: e.target.value
                  })} className="w-full border border-gray-300 rounded-md px-4 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500" data-id="element-2043" />
                    </div>
                  </div>
                  <div data-id="element-2044">
                    <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-2045">
                      Website
                    </label>
                    <input type="url" value={profileData.website} onChange={e => setProfileData({
                  ...profileData,
                  website: e.target.value
                })} className="w-full border border-gray-300 rounded-md px-4 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500" data-id="element-2046" />
                  </div>
                  <div data-id="element-2047">
                    <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-2048">
                      Bio
                    </label>
                    <textarea value={profileData.bio} onChange={e => setProfileData({
                  ...profileData,
                  bio: e.target.value
                })} rows={4} className="w-full border border-gray-300 rounded-md px-4 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500" data-id="element-2049" />
                  </div>
                  <div className="flex justify-end space-x-4 pt-4" data-id="element-2050">
                    <button className="px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50" data-id="element-2051">
                      Cancel
                    </button>
                    <button className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700" data-id="element-2052">
                      Save Changes
                    </button>
                  </div>
                </div>
              </div>}
            {/* Billing Tab */}
            {activeTab === 'billing' && <div className="space-y-6" data-id="element-2053">
                {/* Payment Methods */}
                <div className="bg-white rounded-lg border border-gray-200 p-6" data-id="element-2054">
                  <div className="flex justify-between items-center mb-6" data-id="element-2055">
                    <h2 className="text-xl font-semibold" data-id="element-2056">Payment Methods</h2>
                    <button className="flex items-center px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700" data-id="element-2057">
                      <PlusIcon className="h-4 w-4 mr-2" data-id="element-2058" />
                      Add Method
                    </button>
                  </div>
                  <div className="space-y-4" data-id="element-2059">
                    {paymentMethods.map(method => <div key={method.id} className="flex items-center justify-between p-4 border border-gray-200 rounded-lg" data-id="element-2060">
                        <div className="flex items-center" data-id="element-2061">
                          <CreditCardIcon className="h-6 w-6 text-gray-400 mr-4" data-id="element-2062" />
                          <div data-id="element-2063">
                            <div className="font-medium" data-id="element-2064">
                              {method.type} •••• {method.last4}
                            </div>
                            <div className="text-sm text-gray-500" data-id="element-2065">
                              Expires {method.expiry}
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center space-x-2" data-id="element-2066">
                          {method.isDefault && <span className="px-2 py-1 bg-green-100 text-green-800 text-xs rounded-full" data-id="element-2067">
                              Default
                            </span>}
                          <button className="p-2 text-gray-400 hover:text-red-600" data-id="element-2068">
                            <TrashIcon className="h-4 w-4" data-id="element-2069" />
                          </button>
                        </div>
                      </div>)}
                  </div>
                </div>
                {/* Billing History */}
                <div className="bg-white rounded-lg border border-gray-200 p-6" data-id="element-2070">
                  <h2 className="text-xl font-semibold mb-6" data-id="element-2071">
                    Billing History
                  </h2>
                  <div className="overflow-x-auto" data-id="element-2072">
                    <table className="w-full" data-id="element-2073">
                      <thead data-id="element-2074">
                        <tr className="border-b border-gray-200" data-id="element-2075">
                          <th className="text-left py-3 px-4 text-sm font-medium text-gray-700" data-id="element-2076">
                            Date
                          </th>
                          <th className="text-left py-3 px-4 text-sm font-medium text-gray-700" data-id="element-2077">
                            Amount
                          </th>
                          <th className="text-left py-3 px-4 text-sm font-medium text-gray-700" data-id="element-2078">
                            Status
                          </th>
                          <th className="text-left py-3 px-4 text-sm font-medium text-gray-700" data-id="element-2079">
                            Invoice
                          </th>
                          <th className="text-right py-3 px-4 text-sm font-medium text-gray-700" data-id="element-2080">
                            Action
                          </th>
                        </tr>
                      </thead>
                      <tbody data-id="element-2081">
                        {billingHistory.map(item => <tr key={item.id} className="border-b border-gray-100" data-id="element-2082">
                            <td className="py-3 px-4 text-sm" data-id="element-2083">{item.date}</td>
                            <td className="py-3 px-4 text-sm font-medium" data-id="element-2084">
                              {item.amount}
                            </td>
                            <td className="py-3 px-4" data-id="element-2085">
                              <span className="inline-flex items-center px-2 py-1 bg-green-100 text-green-800 text-xs rounded-full" data-id="element-2086">
                                {item.status}
                              </span>
                            </td>
                            <td className="py-3 px-4 text-sm text-gray-500" data-id="element-2087">
                              {item.invoice}
                            </td>
                            <td className="py-3 px-4 text-right" data-id="element-2088">
                              <button className="text-sm text-indigo-600 hover:text-indigo-700" data-id="element-2089">
                                Download
                              </button>
                            </td>
                          </tr>)}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>}
            {/* Privacy Tab */}
            {activeTab === 'privacy' && <div className="bg-white rounded-lg border border-gray-200 p-6" data-id="element-2090">
                <h2 className="text-xl font-semibold mb-6" data-id="element-2091">Privacy Settings</h2>
                <div className="space-y-6" data-id="element-2092">
                  <div data-id="element-2093">
                    <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-2094">
                      Profile Visibility
                    </label>
                    <select value={privacySettings.profileVisibility} onChange={e => setPrivacySettings({
                  ...privacySettings,
                  profileVisibility: e.target.value
                })} className="w-full border border-gray-300 rounded-md px-4 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500" data-id="element-2095">
                      <option value="public" data-id="element-2096">Public</option>
                      <option value="private" data-id="element-2097">Private</option>
                      <option value="friends" data-id="element-2098">Friends Only</option>
                    </select>
                    <p className="mt-1 text-sm text-gray-500" data-id="element-2099">
                      Control who can see your profile
                    </p>
                  </div>
                  <div className="space-y-4 pt-4 border-t border-gray-200" data-id="element-2100">
                    <label className="flex items-center justify-between" data-id="element-2101">
                      <div data-id="element-2102">
                        <div className="font-medium" data-id="element-2103">Show Email Address</div>
                        <div className="text-sm text-gray-500" data-id="element-2104">
                          Display your email on your profile
                        </div>
                      </div>
                      <button onClick={() => setPrivacySettings({
                    ...privacySettings,
                    showEmail: !privacySettings.showEmail
                  })} className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${privacySettings.showEmail ? 'bg-indigo-600' : 'bg-gray-200'}`} data-id="element-2105">
                        <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${privacySettings.showEmail ? 'translate-x-6' : 'translate-x-1'}`} data-id="element-2106" />
                      </button>
                    </label>
                    <label className="flex items-center justify-between" data-id="element-2107">
                      <div data-id="element-2108">
                        <div className="font-medium" data-id="element-2109">Show My Apps</div>
                        <div className="text-sm text-gray-500" data-id="element-2110">
                          Display your created apps publicly
                        </div>
                      </div>
                      <button onClick={() => setPrivacySettings({
                    ...privacySettings,
                    showApps: !privacySettings.showApps
                  })} className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${privacySettings.showApps ? 'bg-indigo-600' : 'bg-gray-200'}`} data-id="element-2111">
                        <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${privacySettings.showApps ? 'translate-x-6' : 'translate-x-1'}`} data-id="element-2112" />
                      </button>
                    </label>
                    <label className="flex items-center justify-between" data-id="element-2113">
                      <div data-id="element-2114">
                        <div className="font-medium" data-id="element-2115">Allow Messages</div>
                        <div className="text-sm text-gray-500" data-id="element-2116">
                          Let other users send you messages
                        </div>
                      </div>
                      <button onClick={() => setPrivacySettings({
                    ...privacySettings,
                    allowMessages: !privacySettings.allowMessages
                  })} className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${privacySettings.allowMessages ? 'bg-indigo-600' : 'bg-gray-200'}`} data-id="element-2117">
                        <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${privacySettings.allowMessages ? 'translate-x-6' : 'translate-x-1'}`} data-id="element-2118" />
                      </button>
                    </label>
                    <label className="flex items-center justify-between" data-id="element-2119">
                      <div data-id="element-2120">
                        <div className="font-medium" data-id="element-2121">Data Sharing</div>
                        <div className="text-sm text-gray-500" data-id="element-2122">
                          Share usage data to improve services
                        </div>
                      </div>
                      <button onClick={() => setPrivacySettings({
                    ...privacySettings,
                    dataSharing: !privacySettings.dataSharing
                  })} className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${privacySettings.dataSharing ? 'bg-indigo-600' : 'bg-gray-200'}`} data-id="element-2123">
                        <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${privacySettings.dataSharing ? 'translate-x-6' : 'translate-x-1'}`} data-id="element-2124" />
                      </button>
                    </label>
                    <label className="flex items-center justify-between" data-id="element-2125">
                      <div data-id="element-2126">
                        <div className="font-medium" data-id="element-2127">Analytics</div>
                        <div className="text-sm text-gray-500" data-id="element-2128">
                          Help us improve with anonymous analytics
                        </div>
                      </div>
                      <button onClick={() => setPrivacySettings({
                    ...privacySettings,
                    analytics: !privacySettings.analytics
                  })} className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${privacySettings.analytics ? 'bg-indigo-600' : 'bg-gray-200'}`} data-id="element-2129">
                        <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${privacySettings.analytics ? 'translate-x-6' : 'translate-x-1'}`} data-id="element-2130" />
                      </button>
                    </label>
                  </div>
                  <div className="flex justify-end space-x-4 pt-4" data-id="element-2131">
                    <button className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700" data-id="element-2132">
                      Save Changes
                    </button>
                  </div>
                </div>
              </div>}
            {/* Security Tab */}
            {activeTab === 'security' && <div className="space-y-6" data-id="element-2133">
                {/* Change Password */}
                <div className="bg-white rounded-lg border border-gray-200 p-6" data-id="element-2134">
                  <h2 className="text-xl font-semibold mb-6" data-id="element-2135">
                    Change Password
                  </h2>
                  <div className="space-y-4" data-id="element-2136">
                    <div data-id="element-2137">
                      <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-2138">
                        Current Password
                      </label>
                      <div className="relative" data-id="element-2139">
                        <input type={showPassword ? 'text' : 'password'} className="w-full border border-gray-300 rounded-md px-4 py-2 pr-10 focus:outline-none focus:ring-2 focus:ring-indigo-500" data-id="element-2140" />
                        <button onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-2.5 text-gray-400" data-id="element-2141">
                          {showPassword ? <EyeOffIcon className="h-5 w-5" data-id="element-2142" /> : <EyeIcon className="h-5 w-5" data-id="element-2143" />}
                        </button>
                      </div>
                    </div>
                    <div data-id="element-2144">
                      <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-2145">
                        New Password
                      </label>
                      <div className="relative" data-id="element-2146">
                        <input type={showNewPassword ? 'text' : 'password'} className="w-full border border-gray-300 rounded-md px-4 py-2 pr-10 focus:outline-none focus:ring-2 focus:ring-indigo-500" data-id="element-2147" />
                        <button onClick={() => setShowNewPassword(!showNewPassword)} className="absolute right-3 top-2.5 text-gray-400" data-id="element-2148">
                          {showNewPassword ? <EyeOffIcon className="h-5 w-5" data-id="element-2149" /> : <EyeIcon className="h-5 w-5" data-id="element-2150" />}
                        </button>
                      </div>
                    </div>
                    <div data-id="element-2151">
                      <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-2152">
                        Confirm New Password
                      </label>
                      <input type="password" className="w-full border border-gray-300 rounded-md px-4 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500" data-id="element-2153" />
                    </div>
                    <div className="flex justify-end pt-2" data-id="element-2154">
                      <button className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700" data-id="element-2155">
                        Update Password
                      </button>
                    </div>
                  </div>
                </div>
                {/* Two-Factor Authentication */}
                <div className="bg-white rounded-lg border border-gray-200 p-6" data-id="element-2156">
                  <h2 className="text-xl font-semibold mb-6" data-id="element-2157">
                    Two-Factor Authentication
                  </h2>
                  <div className="space-y-4" data-id="element-2158">
                    <label className="flex items-center justify-between" data-id="element-2159">
                      <div data-id="element-2160">
                        <div className="font-medium" data-id="element-2161">Enable 2FA</div>
                        <div className="text-sm text-gray-500" data-id="element-2162">
                          Add an extra layer of security to your account
                        </div>
                      </div>
                      <button onClick={() => setSecuritySettings({
                    ...securitySettings,
                    twoFactorEnabled: !securitySettings.twoFactorEnabled
                  })} className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${securitySettings.twoFactorEnabled ? 'bg-indigo-600' : 'bg-gray-200'}`} data-id="element-2163">
                        <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${securitySettings.twoFactorEnabled ? 'translate-x-6' : 'translate-x-1'}`} data-id="element-2164" />
                      </button>
                    </label>
                    {securitySettings.twoFactorEnabled && <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg" data-id="element-2165">
                        <div className="flex items-start" data-id="element-2166">
                          <AlertCircleIcon className="h-5 w-5 text-blue-600 mr-3 mt-0.5" data-id="element-2167" />
                          <div data-id="element-2168">
                            <p className="text-sm text-blue-900 font-medium" data-id="element-2169">
                              2FA is enabled
                            </p>
                            <p className="text-sm text-blue-700 mt-1" data-id="element-2170">
                              You will need to enter a verification code when
                              logging in
                            </p>
                          </div>
                        </div>
                      </div>}
                  </div>
                </div>
                {/* Security Preferences */}
                <div className="bg-white rounded-lg border border-gray-200 p-6" data-id="element-2171">
                  <h2 className="text-xl font-semibold mb-6" data-id="element-2172">
                    Security Preferences
                  </h2>
                  <div className="space-y-4" data-id="element-2173">
                    <label className="flex items-center justify-between" data-id="element-2174">
                      <div data-id="element-2175">
                        <div className="font-medium" data-id="element-2176">Login Alerts</div>
                        <div className="text-sm text-gray-500" data-id="element-2177">
                          Get notified of new login attempts
                        </div>
                      </div>
                      <button onClick={() => setSecuritySettings({
                    ...securitySettings,
                    loginAlerts: !securitySettings.loginAlerts
                  })} className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${securitySettings.loginAlerts ? 'bg-indigo-600' : 'bg-gray-200'}`} data-id="element-2178">
                        <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${securitySettings.loginAlerts ? 'translate-x-6' : 'translate-x-1'}`} data-id="element-2179" />
                      </button>
                    </label>
                    <div data-id="element-2180">
                      <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-2181">
                        Session Timeout
                      </label>
                      <select value={securitySettings.sessionTimeout} onChange={e => setSecuritySettings({
                    ...securitySettings,
                    sessionTimeout: e.target.value
                  })} className="w-full border border-gray-300 rounded-md px-4 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500" data-id="element-2182">
                        <option value="15" data-id="element-2183">15 minutes</option>
                        <option value="30" data-id="element-2184">30 minutes</option>
                        <option value="60" data-id="element-2185">1 hour</option>
                        <option value="120" data-id="element-2186">2 hours</option>
                      </select>
                      <p className="mt-1 text-sm text-gray-500" data-id="element-2187">
                        Automatically log out after inactivity
                      </p>
                    </div>
                  </div>
                </div>
                {/* Active Sessions */}
                <div className="bg-white rounded-lg border border-gray-200 p-6" data-id="element-2188">
                  <h2 className="text-xl font-semibold mb-6" data-id="element-2189">
                    Active Sessions
                  </h2>
                  <div className="space-y-4" data-id="element-2190">
                    {activeSessions.map(session => <div key={session.id} className="flex items-center justify-between p-4 border border-gray-200 rounded-lg" data-id="element-2191">
                        <div className="flex items-center" data-id="element-2192">
                          <SmartphoneIcon className="h-6 w-6 text-gray-400 mr-4" data-id="element-2193" />
                          <div data-id="element-2194">
                            <div className="font-medium flex items-center" data-id="element-2195">
                              {session.device}
                              {session.current && <span className="ml-2 px-2 py-1 bg-green-100 text-green-800 text-xs rounded-full" data-id="element-2196">
                                  Current
                                </span>}
                            </div>
                            <div className="text-sm text-gray-500" data-id="element-2197">
                              {session.location} • {session.lastActive}
                            </div>
                          </div>
                        </div>
                        {!session.current && <button className="px-3 py-1 text-sm text-red-600 hover:bg-red-50 rounded-md" data-id="element-2198">
                            Revoke
                          </button>}
                      </div>)}
                  </div>
                </div>
              </div>}
            {/* Notifications Tab */}
            {activeTab === 'notifications' && <div className="bg-white rounded-lg border border-gray-200 p-6" data-id="element-2199">
                <h2 className="text-xl font-semibold mb-6" data-id="element-2200">
                  Notification Preferences
                </h2>
                <div className="space-y-4" data-id="element-2201">
                  <label className="flex items-center justify-between" data-id="element-2202">
                    <div data-id="element-2203">
                      <div className="font-medium" data-id="element-2204">Email Notifications</div>
                      <div className="text-sm text-gray-500" data-id="element-2205">
                        Receive notifications via email
                      </div>
                    </div>
                    <button onClick={() => setNotificationSettings({
                  ...notificationSettings,
                  emailNotifications: !notificationSettings.emailNotifications
                })} className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${notificationSettings.emailNotifications ? 'bg-indigo-600' : 'bg-gray-200'}`} data-id="element-2206">
                      <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${notificationSettings.emailNotifications ? 'translate-x-6' : 'translate-x-1'}`} data-id="element-2207" />
                    </button>
                  </label>
                  <label className="flex items-center justify-between" data-id="element-2208">
                    <div data-id="element-2209">
                      <div className="font-medium" data-id="element-2210">App Updates</div>
                      <div className="text-sm text-gray-500" data-id="element-2211">
                        Get notified when your apps are updated
                      </div>
                    </div>
                    <button onClick={() => setNotificationSettings({
                  ...notificationSettings,
                  appUpdates: !notificationSettings.appUpdates
                })} className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${notificationSettings.appUpdates ? 'bg-indigo-600' : 'bg-gray-200'}`} data-id="element-2212">
                      <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${notificationSettings.appUpdates ? 'translate-x-6' : 'translate-x-1'}`} data-id="element-2213" />
                    </button>
                  </label>
                  <label className="flex items-center justify-between" data-id="element-2214">
                    <div data-id="element-2215">
                      <div className="font-medium" data-id="element-2216">New Features</div>
                      <div className="text-sm text-gray-500" data-id="element-2217">
                        Learn about new platform features
                      </div>
                    </div>
                    <button onClick={() => setNotificationSettings({
                  ...notificationSettings,
                  newFeatures: !notificationSettings.newFeatures
                })} className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${notificationSettings.newFeatures ? 'bg-indigo-600' : 'bg-gray-200'}`} data-id="element-2218">
                      <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${notificationSettings.newFeatures ? 'translate-x-6' : 'translate-x-1'}`} data-id="element-2219" />
                    </button>
                  </label>
                  <label className="flex items-center justify-between" data-id="element-2220">
                    <div data-id="element-2221">
                      <div className="font-medium" data-id="element-2222">Marketing Emails</div>
                      <div className="text-sm text-gray-500" data-id="element-2223">
                        Receive promotional content and offers
                      </div>
                    </div>
                    <button onClick={() => setNotificationSettings({
                  ...notificationSettings,
                  marketingEmails: !notificationSettings.marketingEmails
                })} className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${notificationSettings.marketingEmails ? 'bg-indigo-600' : 'bg-gray-200'}`} data-id="element-2224">
                      <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${notificationSettings.marketingEmails ? 'translate-x-6' : 'translate-x-1'}`} data-id="element-2225" />
                    </button>
                  </label>
                  <label className="flex items-center justify-between" data-id="element-2226">
                    <div data-id="element-2227">
                      <div className="font-medium" data-id="element-2228">Weekly Digest</div>
                      <div className="text-sm text-gray-500" data-id="element-2229">
                        Get a weekly summary of your activity
                      </div>
                    </div>
                    <button onClick={() => setNotificationSettings({
                  ...notificationSettings,
                  weeklyDigest: !notificationSettings.weeklyDigest
                })} className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${notificationSettings.weeklyDigest ? 'bg-indigo-600' : 'bg-gray-200'}`} data-id="element-2230">
                      <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${notificationSettings.weeklyDigest ? 'translate-x-6' : 'translate-x-1'}`} data-id="element-2231" />
                    </button>
                  </label>
                  <label className="flex items-center justify-between" data-id="element-2232">
                    <div data-id="element-2233">
                      <div className="font-medium" data-id="element-2234">Security Alerts</div>
                      <div className="text-sm text-gray-500" data-id="element-2235">
                        Important security notifications
                      </div>
                    </div>
                    <button onClick={() => setNotificationSettings({
                  ...notificationSettings,
                  securityAlerts: !notificationSettings.securityAlerts
                })} className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${notificationSettings.securityAlerts ? 'bg-indigo-600' : 'bg-gray-200'}`} data-id="element-2236">
                      <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${notificationSettings.securityAlerts ? 'translate-x-6' : 'translate-x-1'}`} data-id="element-2237" />
                    </button>
                  </label>
                  <div className="flex justify-end space-x-4 pt-4" data-id="element-2238">
                    <button className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700" data-id="element-2239">
                      Save Preferences
                    </button>
                  </div>
                </div>
              </div>}
          </div>
        </div>
      </div>
    </div>;
};